/**
 * Timeline / Race Replay Controls
 * Handles playback, scrubbing, and time-based visualization
 */

export class TimelineController {
  constructor(raceData) {
    this.raceData = raceData;

    // DOM Elements
    this.slider = document.getElementById('timeline-slider');
    this.progress = document.getElementById('timeline-progress');
    this.playPauseBtn = document.getElementById('play-pause');
    this.resetBtn = document.getElementById('reset-time');
    this.liveBtn = document.getElementById('live-btn');
    this.timeCurrent = document.getElementById('time-current');
    this.timeTotal = document.getElementById('time-total');
    this.liveIndicator = document.getElementById('live-indicator');
    this.speedBtns = document.querySelectorAll('.speed-btn');

    // State
    this.isPlaying = false;
    this.isLive = true;
    this.playbackSpeed = 10;
    this.currentTime = null;
    this.animationFrame = null;
    this.lastTick = null;

    // Callbacks
    this.onTimeChange = null;
    this.onLiveChange = null;

    this.init();
  }

  init() {
    // Play/Pause
    this.playPauseBtn.addEventListener('click', () => this.togglePlay());

    // Reset to start
    this.resetBtn.addEventListener('click', () => this.resetToStart());

    // Jump to live
    this.liveBtn.addEventListener('click', () => this.jumpToLive());

    // Slider interaction
    this.slider.addEventListener('input', (e) => this.handleSliderInput(e));
    this.slider.addEventListener('mousedown', () => this.pause());

    // Speed buttons
    this.speedBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const speed = parseInt(btn.dataset.speed, 10);
        this.setSpeed(speed);
      });
    });

    // Update display on data changes
    this.raceData.subscribe((teams, timeRange) => {
      this.updateTimeRange(timeRange);
    });

    // Initialize from existing data (in case fetch already completed)
    if (this.raceData.timeRange.start && this.raceData.timeRange.end) {
      this.updateTimeRange(this.raceData.timeRange);
    }
  }

  /**
   * Update time range from race data
   */
  updateTimeRange(timeRange) {
    if (!timeRange.start || !timeRange.end) return;

    this.timeRange = timeRange;
    this.timeTotal.textContent = this.formatDate(timeRange.end);

    if (this.isLive) {
      this.currentTime = timeRange.end;
      this.updateDisplay();
    }
  }

  /**
   * Toggle play/pause
   */
  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Start playback
   */
  play() {
    if (!this.timeRange) return;

    this.isPlaying = true;
    this.isLive = false;
    this.playPauseBtn.classList.add('playing');
    this.liveBtn.classList.remove('active');
    this.liveIndicator.classList.add('paused');

    // Start from beginning if at end
    if (this.currentTime >= this.timeRange.end) {
      this.currentTime = new Date(this.timeRange.start);
    }

    this.lastTick = performance.now();
    this.animate();

    if (this.onLiveChange) this.onLiveChange(false);
  }

  /**
   * Pause playback
   */
  pause() {
    this.isPlaying = false;
    this.playPauseBtn.classList.remove('playing');

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Animation loop for playback
   */
  animate() {
    if (!this.isPlaying) return;

    const now = performance.now();
    const delta = now - this.lastTick;
    this.lastTick = now;

    // Advance time (speed multiplier converts real seconds to simulated time)
    const advance = delta * this.playbackSpeed;
    this.currentTime = new Date(this.currentTime.getTime() + advance);

    // Check if reached end
    if (this.currentTime >= this.timeRange.end) {
      this.currentTime = this.timeRange.end;
      this.pause();
      this.jumpToLive();
      return;
    }

    this.updateDisplay();
    this.notifyTimeChange();

    this.animationFrame = requestAnimationFrame(() => this.animate());
  }

  /**
   * Handle slider input - RAF-based for smooth scrubbing
   */
  handleSliderInput(e) {
    if (!this.timeRange) return;

    const percent = parseFloat(e.target.value) / 100;

    // If slider is at 100%, jump to live mode
    if (percent >= 0.999) {
      this.jumpToLive();
      return;
    }

    const duration = this.timeRange.end - this.timeRange.start;
    this.currentTime = new Date(this.timeRange.start.getTime() + duration * percent);

    if (this.isLive) {
      this.isLive = false;
      this.liveBtn.classList.remove('active');
      this.liveIndicator.classList.add('paused');
      if (this.onLiveChange) this.onLiveChange(false);
    }

    // Use RAF for smooth updates during scrubbing
    if (!this._scrubRaf) {
      this._scrubRaf = requestAnimationFrame(() => {
        this._scrubRaf = null;
        this.updateDisplay();
        this.notifyTimeChange();
      });
    }
  }

  /**
   * Reset to start of race
   */
  resetToStart() {
    if (!this.timeRange) return;

    this.pause();
    this.currentTime = new Date(this.timeRange.start);
    this.isLive = false;
    this.liveBtn.classList.remove('active');
    this.liveIndicator.classList.add('paused');

    this.updateDisplay();
    this.notifyTimeChange();

    if (this.onLiveChange) this.onLiveChange(false);
  }

  /**
   * Jump to live (latest) time
   */
  jumpToLive() {
    if (!this.timeRange) return;

    this.pause();
    this.currentTime = this.timeRange.end;
    this.isLive = true;
    this.liveBtn.classList.add('active');
    this.liveIndicator.classList.remove('paused');

    this.updateDisplay();

    // Don't call notifyTimeChange() - live mode uses raceData.teams directly
    // The onLiveChange handler will render the live data
    if (this.onLiveChange) this.onLiveChange(true);
  }

  /**
   * Set playback speed
   */
  setSpeed(speed) {
    this.playbackSpeed = speed;

    this.speedBtns.forEach(btn => {
      btn.classList.toggle('active', parseInt(btn.dataset.speed, 10) === speed);
    });
  }

  /**
   * Update display elements
   */
  updateDisplay() {
    if (!this.timeRange || !this.currentTime) return;

    const duration = this.timeRange.end - this.timeRange.start;
    const elapsed = this.currentTime - this.timeRange.start;
    const percent = Math.max(0, Math.min(100, (elapsed / duration) * 100));

    this.slider.value = percent;
    this.progress.style.setProperty('--progress', `${percent}%`);
    this.timeCurrent.textContent = this.formatDate(this.currentTime);
  }

  /**
   * Format date for display
   */
  formatDate(date) {
    if (!date) return '--';

    const options = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  /**
   * Notify time change callback
   */
  notifyTimeChange() {
    if (this.onTimeChange && this.currentTime) {
      this.onTimeChange(this.currentTime);
    }
  }

  /**
   * Set time change handler
   */
  setTimeChangeHandler(handler) {
    this.onTimeChange = handler;
  }

  /**
   * Set live change handler
   */
  setLiveChangeHandler(handler) {
    this.onLiveChange = handler;
  }

  /**
   * Get current time
   */
  getCurrentTime() {
    return this.currentTime;
  }

  /**
   * Check if in live mode
   */
  getIsLive() {
    return this.isLive;
  }

  /**
   * Cleanup
   */
  destroy() {
    this.pause();
  }
}
